// PackageScanner.jsx
import React, { useState, useEffect, useRef } from 'react'
import QrScanner from 'react-qr-scanner'
import Tesseract from 'tesseract.js'
import { Button, Box } from '@mui/material'
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

/**
 * Props:
 *   onBackClick: function to call when user wants to go back
 *   userId: the current user's ID, to send as `scanned_by`
 */
export default function PackageScanner({ onBackClick, userId }) {
  // modes: 'idle' | 'qr' | 'number'
  const [mode, setMode] = useState('idle')
  const [showSuccess, setShowSuccess] = useState(false)
  const videoRef = useRef(null)
  const canvasRef = useRef(null)
  const streamRef = useRef(null)
  const [manualId, setManualId] = useState('');
  const [driverId, setDriverId] = useState(null);
  const [district, setDistrict] = useState('');
  const [status, setStatus] = useState('');

  const [backCameraId, setBackCameraId] = useState(null)

  useEffect(() => {
    navigator.mediaDevices
      .getUserMedia({ video: true })
      .then(() => navigator.mediaDevices.enumerateDevices())
      .then(devices => {
        const rear = devices.find(d =>
          d.kind === 'videoinput' &&
          /rear|back|environment/i.test(d.label)
        )
        setBackCameraId(rear?.deviceId || null)
      })
      .catch(err => console.error('Camera enumeration failed', err))
  }, [])


  // Auto-hide success banner after 2s
  useEffect(() => {
    if (!showSuccess) return
    const timer = setTimeout(() => setShowSuccess(false), 3000)
    return () => clearTimeout(timer)
  }, [showSuccess])

  // Start camera for number scanning
  useEffect(() => {
    if (mode !== 'number') return
    navigator.mediaDevices
      .getUserMedia({ video: { facingMode: 'environment' } })
      .then(stream => {
        streamRef.current = stream
        if (videoRef.current) {
          videoRef.current.srcObject = stream
          videoRef.current.play()
        }
      })
      .catch(err => {
        console.error('Camera error:', err)
        setMode('idle')
      })

    return () => {
      // cleanup on exit
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop())
        streamRef.current = null
      }
    }
  }, [mode])

  // Generic status update
  const updateStatus = async (identifier) => {
    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/api/packages/${encodeURIComponent(identifier)}/status`,
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ new_status: 'in_fastmax', user_id: userId })
        }
      )
      if (!res.ok) {
          console.error('Update failed', await res.text());
          return;
        }

        const data = await res.json();
        setDriverId(data.driver_id);
        setDistrict(data.district);
        setStatus(data.status);
        setShowSuccess(true);
      } catch (err) {
        console.error('Network error', err);
      }
  }

  // Handle QR scanning
  const handleQrScan = (result) => {
    if (!result) return
    const code = typeof result === 'string' ? result : result?.text
    if (!code) return
    setMode('idle')
    updateStatus(code)
  }

  // Handle number capture & OCR
  const handleNumberScan = async () => {
    if (!videoRef.current || !canvasRef.current) return
    const video = videoRef.current
    const canvas = canvasRef.current
    canvas.width = video.videoWidth
    canvas.height = video.videoHeight
    const ctx = canvas.getContext('2d')
    ctx.drawImage(video, 0, 0)

    // OCR
    const { data: { text }} = await Tesseract.recognize(canvas, 'eng')
    const match = text.match(/\d+/)
    if (!match) {
      alert('No number detected—try again.')
      return
    }

    const id = match[0]
    if (!window.confirm(`Detected ID: ${id}\nProceed to update status?`)) {
      return
    }

    setMode('idle')
    updateStatus(id)
  }

  return (
    <div style={{
      position: 'relative',
      height: '100vh',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '1em'
    }}>
      <Box sx={{ marginBottom: 2 }}>
        <ArrowBackIcon onClick={onBackClick} style={{ color: 'black' }}/>

        </Box>
      {/* Success banner */}
      {showSuccess && (
        <div style={{
          position: 'absolute',
          top: 0,
          width: '100%',
          padding: '1em',
          backgroundColor: '#d4edda',
          color: '#155724',
          textAlign: 'center',
          zIndex: 1000
        }}>
          <strong>Status updated!</strong>
          <br/>Driver ID: {driverId}<br/>
          District: {district}<br/>
          Estado: {status}
        </div>
      )}

      {/* Back button */}
     

      {/* QR scanner view */}
      {mode === 'qr' && (
        <div style={{ width: '100%', maxWidth: 400, textAlign: 'center' }}>
          <QrScanner
            delay={300}
            onError={e=>console.error(e)}
            onScan={handleQrScan}
            style={{ width:'100%' }}
            // 2) feed the exact deviceId if we found it, else fallback to "environment"
            constraints={{
              video: backCameraId
                ? { deviceId: { exact: backCameraId } }
                : { facingMode: { ideal: 'environment' } }
            }}
          />
          <Button 
            variant="outlined"
            color="primary"
            onClick={() => setMode('idle')} style={{ marginTop: 10 }}>
            Cancel
          </Button>
        </div>
      )}

      {/* Number scanning view */}
      {mode === 'number' && (
        <div style={{ textAlign: 'center' }}>
          <video ref={videoRef} style={{ width: '100%', maxWidth: 400 }} />
          <canvas ref={canvasRef} style={{ display: 'none' }} />
          <div style={{ marginTop: 10 }}>
            <Button 
              variant="contained"
              color="primary"
              onClick={handleNumberScan} style={{ marginRight: 10 }}>
              Scan Number
            </Button>
            <Button 
              variant="outlined"
              color="secondary"
              onClick={() => setMode('idle')}>
              Cancel
            </Button>
          </div>
        </div>
      )}

      {mode === 'addNumber' && (
        <div style={{ textAlign: 'center', marginTop: '1rem' }}>
          <input
            type="text"
            placeholder="Enter numeric ID"
            value={manualId}
            onChange={e => {
              // allow only digits
              setManualId(e.target.value.replace(/\D/g, ''));
            }}
            style={{ fontSize: '1.2em', padding: '0.5em', width: '8em' }}
          />
          <div style={{ marginTop: '1em' }}>
            <Button 
              variant="contained"
              color="primary"
              onClick={() => {
                if (!manualId) {
                  alert('Please enter a numeric ID');
                  return;
                }
                setMode('idle');
                updateStatus(manualId);
                setManualId('');
              }}
              style={{ marginRight: '0.5em' }}
            >
              OK
            </Button>
            <Button
              variant="outlined"
              color="secondary"
              onClick={() => {
                setMode('idle');
                setManualId('');
              }}
            >
              Cancel
            </Button>
          </div>
        </div>
      )}

      {/* Idle state: show both buttons */}
      {mode === 'idle' && (
        <div style={{ display: 'flex', gap: 20 }}>
          <button
            onClick={() => setMode('qr')}
            style={{
              padding: '1em 2em', fontSize: '1.2em', border: 'none',
              borderRadius: 4, backgroundColor: '#007bff', color: 'white', cursor: 'pointer'
            }}
          >
            Scanear QR
          </button>
          <button
            onClick={() => setMode('number')}
            style={{
              padding: '1em 2em', fontSize: '1.2em', border: 'none',
              borderRadius: 4, backgroundColor: '#28a745', color: 'white', cursor: 'pointer'
            }}
          >
            Scanear Codigo
          </button>
          <button
            onClick={() => setMode('addNumber')}
            style={{
              padding: '1em 2em', fontSize: '1.2em', border: 'none',
              borderRadius: 4, backgroundColor: '#28a745', color: 'white', cursor: 'pointer'
            }}
          >
            Ingresar Codigo
          </button>
        </div>
      )}
    </div>
  )
}
